import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  LocationSelection,
  LocationWithSimulatorSelection,
} from 'src/app/shared/models/location.model';
import {
  SimulatorResponse,
  SimulatorSelectList,
} from 'src/app/shared/models/simulators.model';
import { compareLauncherVersion } from 'src/app/shared/helper-functions';

const hasReportedVersion = (simulator: { launcherVersion?: string }): boolean =>
  !!simulator?.launcherVersion?.trim();

@Injectable({
  providedIn: 'root',
})
export class SimulatorsService {
  constructor(private httpClient: HttpClient) {}

  public getSimulatorsByLocationId(
    locationId: number,
  ): Observable<SimulatorResponse[]> {
    const url =
      environment.apiServer +
      `/Simulator/GetSimulatorsByLocationId/${locationId}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<SimulatorResponse[]>(url, { headers: headers })
      .pipe(map((res) => (res || []).filter(hasReportedVersion)));
  }

  public getBayManagerLocationIds(): Observable<Set<number>> {
    const url = environment.apiServer + `/Locations/MyLocations`;
    return this.httpClient
      .get<{ id: number; isBayManager: boolean }[]>(url)
      .pipe(
        map(
          (res) =>
            new Set((res || []).filter((l) => l.isBayManager).map((l) => l.id)),
        ),
      );
  }

  public getLocationsSelectList(
    company_id: number,
  ): Observable<LocationSelection[]> {
    const url =
      environment.apiServer + `/Locations/GetLocationsSelectList/${company_id}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<LocationSelection[]>(url, { headers: headers })
      .pipe((res) => {
        const p = res || null;
        return p;
      });
  }

  public getSimulatorsSelectListByCompanyId(
    companyId: number,
  ): Observable<SimulatorSelectList[]> {
    const url =
      environment.apiServer +
      `/Simulator/GetSimulatorsSelectListByCompanyId/${companyId}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<SimulatorSelectList[]>(url, { headers: headers });
  }

  public getLocationsAndSimulatorsSelection(
    company_id: number,
  ): Observable<LocationWithSimulatorSelection[]> {
    const url =
      environment.apiServer +
      `/Locations/GetLocationsAndSimulatorsSelection/${company_id}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<LocationWithSimulatorSelection[]>(url, { headers: headers })
      .pipe(
        map((res) => {
          const p = (res || []).map((location) => {
            location.simulators = (location.simulators || []).filter(
              hasReportedVersion,
            );
            return location;
          });
          return compareLauncherVersion(p);
        }),
      );
  }

  public getSimulatorbyId(identifier: number): Observable<SimulatorResponse> {
    const url = environment.apiServer + `/Simulator/${identifier}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<SimulatorResponse>(url, { headers: headers })
      .pipe((res) => {
        const p = res || null;
        return p;
      });
  }

  public getSimulatorByIdentifier(
    identifier: string,
  ): Observable<SimulatorResponse> {
    const url =
      environment.apiServer +
      `/Simulator/GetSimulatorByIdentifier/${identifier}`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .get<SimulatorResponse>(url, { headers: headers })
      .pipe(map((res) => (res && hasReportedVersion(res) ? res : null)));
  }

  public getSimulatorByRangeIdentifier(
    identifiers: string[],
  ): Observable<SimulatorResponse[]> {
    const url =
      environment.apiServer + `/Simulator/GetSimulatorByRangeIdentifier`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json; charset=utf-8');

    return this.httpClient
      .post<SimulatorResponse[]>(url, identifiers, {
        headers: headers,
      })
      .pipe(map((res) => (res || []).filter(hasReportedVersion)));
  }
}
